import { memo, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import {
  LEVEL_LABELS,
  OWNER_LABELS,
  type CellLevel,
  type DiscoveryGrid as DiscoveryGridData,
  type GridBlock,
  type GridCell,
  type GridRegion,
  type GridSummary,
} from '../utils/discoveryGrid';

/**
 * The discovery grid: regions (CI categories) → blocks (config items) → cells
 * (questions and artifacts), the summary band and the legend
 * (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §4.5–§4.7). Rendering only: the host
 * (ClientConfigItemsTable) owns data loading, filters and what a click opens.
 *
 * Filters DIM cells rather than hide them, so the map stays whole (§4.8).
 */

export type GridDensity = 'compact' | 'comfortable' | 'spacious';

/** Lenses (spec §4.4, §4.6, §4.10): Progress shades by level; Ball-in-court colors by owner; Mine fades what is not yours; Scope source colors by document. */
export type GridLens = 'progress' | 'court' | 'mine' | 'source';

export const COURT_FILL: Record<'client' | 'consultant' | 'none' | 'blocked', string> = {
  client: '#E07A1F',
  consultant: '#6D4AE8',
  none: '#E5E7EB',
  blocked: '#111827',
};

export const SOURCE_FILL: Record<'sow' | 'change_order' | 'carried_over', string> = {
  sow: '#9CA3AF',
  change_order: '#0D9488',
  carried_over: '#2563EB',
};

/** Hollow (descoped) cells: white fill, a border that names where they went. */
export const DESCOPED_BORDER: Record<'document' | 'other', string> = {
  document: '#2563EB', // to a CO or the next engagement
  other: '#6B7280', // handed to the client, or dropped
};

export const LENS_HINTS: Record<GridLens, string> = {
  progress: 'Shade is how settled each cell is: light gray not started, darkest locked for build. Amber is blocked.',
  court: 'Orange is waiting on the client, purple on the consultant (answers not yet checked, plus blockers), gray needs nothing, near-black is blocked.',
  mine: 'Cells on config items assigned to you stay bright; everything else fades.',
  source: 'Gray came with the SOW, teal was added by a change order, blue was carried over. Hollow cells left this engagement: blue border to a CO or the next engagement, dashed to the client or dropped.',
};

export function cellFill(cell: GridCell, lens: GridLens): string {
  if (lens === 'court') return cell.blocked ? COURT_FILL.blocked : COURT_FILL[cell.owner];
  if (lens === 'source') return SOURCE_FILL[cell.source.type] ?? SOURCE_FILL.sow;
  return cell.blocked ? BLOCKED_FILL : LEVEL_FILL[cell.level];
}

export function descopedBorder(cell: GridCell): string {
  const toDocument = cell.descoped?.to === 'change_order' || cell.descoped?.to === 'engagement';
  return `1.5px ${toDocument ? 'solid' : 'dashed'} ${toDocument ? DESCOPED_BORDER.document : DESCOPED_BORDER.other}`;
}

/** One hue, six lightness steps (spec §4.2); blocked is the only non-blue. */
export const LEVEL_FILL: Record<CellLevel, string> = {
  0: '#E5E7EB',
  1: '#C7E4F3',
  2: '#8CC8E6',
  3: '#4FA3CF',
  4: '#1F7FA6',
  5: '#0B4F6C',
};
export const BLOCKED_FILL = '#F59E0B';

const DENSITY = {
  compact: { size: 12, gap: 2 },
  comfortable: { size: 18, gap: 3 },
  /** Small engagements: cells big enough to carry a short label (host picks this via auto density). */
  spacious: { size: 64, gap: 6 },
} as const;

/** Cells at or above this size show the start of their question inside the square. */
export const LABEL_MIN_SIZE = 48;

function isLightFill(hex: string): boolean {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return true;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}

export interface DiscoveryGridProps {
  data: DiscoveryGridData | null;
  loading: boolean;
  error?: string | null;
  density: GridDensity;
  lens?: GridLens;
  /** For the Mine lens: blocks assigned to this user stay bright. */
  viewerId?: string;
  /** Returns true when a cell passes the host's filters; non-matching cells are dimmed. */
  matches?: (cell: GridCell, block: GridBlock, region: GridRegion) => boolean;
  selectedCellId?: string | null;
  onCellClick: (cell: GridCell, block: GridBlock, region: GridRegion) => void;
  viewerRole: 'consultant' | 'client';
  /** Shown under the regions when there are config items without cells. */
  notActionableCount?: number;
  onShowNotActionable?: () => void;
}

export function cellTitle(cell: GridCell, block: GridBlock): string {
  const parts = [cell.text, block.name, cell.levelLabel, `Ball: ${OWNER_LABELS[cell.owner]}`];
  if (cell.blocked && cell.gatingIssues[0]) parts.splice(3, 0, `Blocked: ${cell.gatingIssues[0].message}`);
  if (cell.source.type !== 'sow' && cell.source.label) parts.push(cell.source.label);
  return parts.join(' · ');
}

const Cell = memo(function Cell({
  cell,
  block,
  size,
  dimmed,
  selected,
  lens,
  onClick,
}: {
  cell: GridCell;
  block: GridBlock;
  size: number;
  dimmed: boolean;
  selected: boolean;
  lens: GridLens;
  onClick: () => void;
}) {
  const hollow = !!cell.descoped;
  const fill = hollow ? '#FFFFFF' : cellFill(cell, lens);
  const title = cellTitle(cell, block) + (hollow ? ` · Descoped: ${cell.descoped?.label ?? cell.descoped?.to}` : '') + (cell.recommended ? ' · Recommended next' : '') + (cell.changed ? ' · Changed since your last visit' : '');
  // Rings are drawn INSIDE the cell (inset) so the cell keeps its footprint and silhouette:
  // the outer pixels of the square or circle take the ring color instead of a halo around it.
  const ringWidth = size <= 14 ? 1 : Math.min(3, Math.max(1.5, Math.round(size / 8)));
  const ringColor = selected ? '#111827' : cell.recommended && lens === 'progress' && !hollow ? '#FACC15' : null;
  const ring = ringColor ? `inset 0 0 0 ${ringWidth}px ${ringColor}` : undefined;
  const dotColor = cell.changed && !hollow ? (lens === 'court' || cell.level >= 4 || cell.blocked ? '#FFFFFF' : '#0B4F6C') : undefined;
  const labelled = size >= LABEL_MIN_SIZE;
  const labelColor = hollow || isLightFill(fill) ? '#111827' : '#FFFFFF';
  return (
    <button
      type="button"
      className={`block p-0 cursor-pointer transition-transform duration-75 ${labelled ? 'hover:scale-105' : 'hover:scale-150'} hover:z-10 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-900 ${cell.kind === 'artifact' ? 'rounded-full' : 'rounded-[3px]'}`}
      style={{
        width: size,
        height: size,
        backgroundColor: fill,
        border: hollow ? descopedBorder(cell) : '0',
        boxSizing: 'border-box',
        opacity: dimmed ? 0.18 : 1,
        boxShadow: ring,
        position: 'relative',
      }}
      data-descoped={hollow ? '1' : undefined}
      title={title}
      aria-label={title}
      aria-pressed={selected}
      data-cell-id={cell.id}
      data-recommended={cell.recommended ? '1' : undefined}
      data-changed={cell.changed ? '1' : undefined}
      onClick={onClick}
    >
      {labelled && (
        <span aria-hidden="true" className={`absolute inset-0 flex p-1.5 text-[10px] leading-[12px] font-medium overflow-hidden ${cell.kind === 'artifact' ? 'items-center justify-center text-center' : 'items-end text-left'}`} style={{ color: labelColor, opacity: dimmed ? 0.6 : 0.95 }}>
          <span className="line-clamp-3 break-words">{cell.text}</span>
        </span>
      )}
      {dotColor && <span aria-hidden="true" style={{ position: 'absolute', top: labelled ? 4 : 1, right: labelled ? 4 : 1, width: labelled ? 8 : Math.max(3, Math.round(size / 4)), height: labelled ? 8 : Math.max(3, Math.round(size / 4)), borderRadius: '50%', backgroundColor: dotColor }} />}
    </button>
  );
});

/**
 * Keyboard navigation (spec §4.8): arrows move within a block (by its columns),
 * Tab moves between blocks. The first cell of each block is the tab stop.
 */
function handleGridKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
  const target = e.target as HTMLElement;
  if (!target?.dataset?.cellId) return;
  const blockEl = target.closest('[data-block-id]') as HTMLElement | null;
  if (!blockEl) return;
  const cells = Array.from(blockEl.querySelectorAll<HTMLButtonElement>('button[data-cell-id]'));
  const cols = Number(blockEl.dataset.cols || '1') || 1;
  const i = cells.indexOf(target as HTMLButtonElement);
  if (i === -1) return;
  let next = -1;
  switch (e.key) {
    case 'ArrowRight': next = i + 1; break;
    case 'ArrowLeft': next = i - 1; break;
    case 'ArrowDown': next = i + cols; break;
    case 'ArrowUp': next = i - cols; break;
    case 'Home': next = 0; break;
    case 'End': next = cells.length - 1; break;
    default: return;
  }
  if (next < 0 || next >= cells.length) return;
  e.preventDefault();
  cells[next].focus();
}

function Block({
  block,
  region,
  density,
  lens,
  viewerId,
  matches,
  selectedCellId,
  onCellClick,
}: {
  block: GridBlock;
  region: GridRegion;
  density: GridDensity;
  lens: GridLens;
  viewerId?: string;
  matches?: DiscoveryGridProps['matches'];
  selectedCellId?: string | null;
  onCellClick: DiscoveryGridProps['onCellClick'];
}) {
  const { size, gap } = DENSITY[density];
  // Labelled (spacious) cells read better in wider rows, and a small engagement has the room.
  const cols = density === 'spacious' ? Math.min(block.cells.length, Math.max(block.cols, 8)) : block.cols;
  const width = cols * size + (cols - 1) * gap;
  const tag = block.source.type === 'change_order' ? (block.source.ref ?? 'CO') : block.source.type === 'carried_over' ? 'Carried over' : block.guided ? 'Guided' : null;
  const mineFade = lens === 'mine' && !(viewerId && block.assignedTo === viewerId);
  return (
    <div className="flex flex-col gap-1 rounded-md p-1.5 hover:bg-white hover:shadow-sm transition-colors" style={{ width: width + 12 }} data-block-id={block.configItemId} data-cols={cols}>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, ${size}px)`, gap }} role="group" aria-label={block.name}>
        {block.cells.map((cell) => (
          <Cell
            key={cell.id}
            cell={cell}
            block={block}
            size={size}
            lens={lens}
            dimmed={mineFade || (matches ? !matches(cell, block, region) : false)}
            selected={selectedCellId === cell.id}
            onClick={() => onCellClick(cell, block, region)}
          />
        ))}
      </div>
      <div className="text-[10px] leading-tight text-gray-500 flex flex-wrap items-center gap-1" title={block.name}>
        <span className="line-clamp-2">{block.name}</span>
        {tag && <span className={`text-[9px] font-semibold px-1 rounded ${block.guided ? 'bg-gray-100 text-gray-600' : 'bg-teal-100 text-teal-900'}`}>{tag}</span>}
      </div>
    </div>
  );
}

export interface LegendRow { fill: string; count: number; label: string; border: boolean }

/**
 * Progress-lens legend: the six levels (locked first), blocked, and descoped
 * when any. Shared by the grid's summary band and the engagement card's hover
 * snapshot so the two never disagree on names or colors.
 */
export function progressLegendRows(summary: GridSummary): LegendRow[] {
  const levels: CellLevel[] = [5, 4, 3, 2, 1, 0];
  return [
    ...levels.map((l) => ({ fill: LEVEL_FILL[l], count: summary.byLevel[l], label: `${LEVEL_LABELS[l]}${l === 2 ? ' / artifact in progress' : l === 4 ? ' / artifact complete' : ''}`, border: l === 0 })),
    { fill: BLOCKED_FILL, count: summary.blocked, label: 'Blocked (any level)', border: false },
    ...(summary.descoped > 0 ? [{ fill: '#FFFFFF', count: summary.descoped, label: 'Descoped (hollow, not counted)', border: true }] : []),
  ];
}

export function SummaryBand({ summary, viewerRole, lens = 'progress' }: { summary: GridSummary; viewerRole: 'consultant' | 'client'; lens?: GridLens }) {
  const circumference = 2 * Math.PI * 36;
  const dash = `${((summary.percentLocked / 100) * circumference).toFixed(1)} ${circumference.toFixed(1)}`;
  const yourCourt = viewerRole === 'client' ? summary.court.client : summary.court.consultant;
  const legend = lens === 'court'
    ? [
        { fill: COURT_FILL.client, count: summary.court.client, label: 'Waiting on the client', border: false },
        { fill: COURT_FILL.consultant, count: summary.court.consultant, label: 'Waiting on the consultant (answers to check, blockers)', border: false },
        { fill: COURT_FILL.none, count: summary.court.none, label: 'Nothing owed', border: true },
        { fill: COURT_FILL.blocked, count: summary.blocked, label: 'Blocked', border: false },
      ]
    : lens === 'source'
    ? [
        { fill: SOURCE_FILL.sow, count: summary.total, label: 'In scope (SOW, change order, carried over by color)', border: false },
        { fill: '#FFFFFF', count: summary.descoped, label: 'Descoped (hollow, not counted)', border: true },
      ]
    : progressLegendRows(summary);
  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="flex items-center gap-3">
        <svg width="84" height="84" viewBox="0 0 84 84" role="img" aria-label={`${summary.percentLocked} percent locked for build`}>
          <circle cx="42" cy="42" r="36" fill="none" stroke="#E5E7EB" strokeWidth="9" />
          <circle cx="42" cy="42" r="36" fill="none" stroke={LEVEL_FILL[5]} strokeWidth="9" strokeDasharray={dash} transform="rotate(-90 42 42)" />
          <text x="42" y="40" textAnchor="middle" fontSize="18" fontWeight="700" fill="#111827">{summary.percentLocked}%</text>
          <text x="42" y="55" textAnchor="middle" fontSize="9" fill="#6B7280">locked</text>
        </svg>
        <div className="flex flex-col gap-0.5 text-xs text-gray-700">
          {legend.map((row) => (
            <div key={row.label} className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-[2px]" style={{ backgroundColor: row.fill, border: row.border ? '1px solid #D1D5DB' : undefined }} />
              <span className="min-w-[28px] text-right font-semibold tabular-nums">{row.count}</span>
              <span>{row.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1 text-xs text-gray-700 min-w-[200px]">
        <div><span className="font-semibold text-gray-900">{summary.total}</span> questions and artifacts</div>
        <div><span className="font-semibold text-amber-700">{yourCourt}</span> waiting on you</div>
        <div><span className="font-semibold">{viewerRole === 'client' ? summary.court.consultant : summary.court.client}</span> waiting on the {viewerRole === 'client' ? 'consultant' : 'client'}</div>
        <div className="flex items-center gap-2 mt-1"><span className="inline-block w-3 h-3 rounded-[2px]" style={{ backgroundColor: LEVEL_FILL[3] }} /><span>Square = question</span></div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: LEVEL_FILL[3] }} /><span>Circle = artifact (wizard or configuration)</span></div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-[2px]" style={{ backgroundColor: LEVEL_FILL[0], boxShadow: 'inset 0 0 0 2px #FACC15' }} /><span>Yellow ring = recommended next for you</span></div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-[2px] relative" style={{ backgroundColor: LEVEL_FILL[0] }}><span className="absolute top-[1px] right-[1px] w-1 h-1 rounded-full" style={{ backgroundColor: LEVEL_FILL[5] }} /></span><span>Dot = changed since your last visit</span></div>
      </div>
    </div>
  );
}

export function DiscoveryGrid({ data, loading, error, density, lens = 'progress', viewerId, matches, selectedCellId, onCellClick, viewerRole, notActionableCount = 0, onShowNotActionable }: DiscoveryGridProps) {
  const regions = data?.regions ?? [];
  const matchCount = useMemo(() => {
    if (!matches) return null;
    let n = 0;
    for (const r of regions) for (const b of r.blocks) for (const c of b.cells) if (matches(c, b, r)) n++;
    return n;
  }, [regions, matches]);

  if (error) {
    return (
      <div className="h-64 flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-gray-200 rounded-xl gap-2">
        <AlertTriangle className="w-8 h-8 text-amber-500" />
        <p className="font-medium">The discovery grid could not be loaded</p>
        <p className="text-xs">{error}</p>
      </div>
    );
  }

  if (loading && !data) {
    return <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Loading the discovery grid…</div>;
  }

  if (regions.length === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-gray-200 rounded-xl">
        <p className="font-medium">No discovery cells yet</p>
        <p className="text-xs mt-1">{viewerRole === 'client' ? 'Your consultant is preparing the questions for this engagement.' : 'Import a baseline or add questions to config items to populate the grid.'}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" onKeyDown={handleGridKeyDown}>
      {matchCount !== null && matchCount !== (data?.summary.total ?? 0) && (
        <div className="text-xs text-gray-500">{matchCount} of {data?.summary.total} questions and artifacts match the current filters; the rest are dimmed, not hidden.</div>
      )}
      {regions.map((region) => (
        <section key={region.category} data-region={region.category}>
          <div className="flex flex-wrap items-baseline gap-3 mb-1">
            <h3 className="text-base font-semibold text-gray-900">{formatCategoryLabel(region.category) || region.label}</h3>
            <span className="text-[11px] text-gray-500 tabular-nums">{region.locked} of {region.total} locked</span>
            <span className="inline-block w-[110px] h-1 bg-gray-200 rounded overflow-hidden"><span className="block h-1" style={{ width: `${region.total ? Math.round((region.locked / region.total) * 100) : 0}%`, backgroundColor: LEVEL_FILL[5] }} /></span>
            {region.blocked > 0 && <span className="text-[11px] text-amber-700">{region.blocked} blocked</span>}
          </div>
          <div className="flex flex-wrap gap-2 items-start">
            {region.blocks.map((block) => (
              <Block key={block.configItemId} block={block} region={region} density={density} lens={lens} viewerId={viewerId} matches={matches} selectedCellId={selectedCellId} onCellClick={onCellClick} />
            ))}
          </div>
        </section>
      ))}
      {notActionableCount > 0 && (
        <button type="button" onClick={onShowNotActionable} className="self-start text-[11px] text-gray-400 hover:text-gray-600 underline-offset-2 hover:underline">
          {notActionableCount} config item{notActionableCount === 1 ? '' : 's'} with no asked questions yet (not drawn)
        </button>
      )}
    </div>
  );
}
